//MessageInput.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { WebSocketContext } from './WebSocketProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faArrowUp, faClock } from '@fortawesome/free-solid-svg-icons';
import Compressor from 'compressorjs';

const MessageInput = ({ clientPhone, displayName, sessionId, threadId, threads }) => {
    const [text, setText] = useState('');
    const { sendMessage } = useContext(WebSocketContext);
    const [previewImage, setPreviewImage] = useState(null);
    const [compressedFile, setCompressedFile] = useState(null);
    const [isMobile, setIsMobile] = useState(true);
    const [showIntervalDropdown, setShowIntervalDropdown] = useState(false);
    const [selectedInterval, setSelectedInterval] = useState(null);
    const [isCustomInterval, setIsCustomInterval] = useState(false);
    const dropdownRef = useRef(null);

    // Time interval options in milliseconds - same as in ChatConfigModal
    const intervalOptions = [
        { value: '5000', label: '5 seconds' },
        { value: '30000', label: '30 seconds' },
        { value: '3600000', label: '1 hour' },
        { value: '86400000', label: '1 day' },
        { value: '2170691800', label: '25 days' },
        { value: 'none', label: 'None' }
    ];

    // Detect mobile view
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle clicks outside the dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowIntervalDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Set the current thread's interval as default when thread changes
    useEffect(() => {
        if (threadId && threads) {
            const currentThread = threads.find(thread => thread.id === threadId);
            if (currentThread && currentThread.config && currentThread.config.unhiddenInterval) {
                setSelectedInterval(currentThread.config.unhiddenInterval.toString());
                setIsCustomInterval(false);
            } else {
                // Default to 1 day if no interval is set
                setSelectedInterval('86400000');
                setIsCustomInterval(false);
            }
        }
    }, [threadId, threads]);

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            new Compressor(file, {
                quality: 0.6, // Compress to 60% of original quality
                success(result) {
                    stripMetadata(result);
                },
                error(err) {
                    console.error('Error compressing image:', err);
                },
            });
        }
    };

    const chunkBase64 = (base64String, chunkSize = 16000) => {
        const chunks = [];
        for (let i = 0; i < base64String.length; i += chunkSize) {
            chunks.push(base64String.slice(i, i + chunkSize));
        }
        return chunks;
    };

    const stripMetadata = (file) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Calculate the new dimensions
                const maxSize = 1600; // Maximum size for the greater of height or width
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxSize) {
                        height *= maxSize / width;
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width *= maxSize / height;
                        height = maxSize;
                    }
                }

                // Set canvas dimensions to the new dimensions
                canvas.width = width;
                canvas.height = height;

                // Draw the image with the new dimensions
                ctx.drawImage(img, 0, 0, width, height);

                // Convert the canvas to a blob and create a new file
                canvas.toBlob(
                    (blob) => {
                        const strippedFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                        });
                        setCompressedFile(strippedFile);
                        setPreviewImage(URL.createObjectURL(blob)); // Create a preview URL for the image
                    },
                    'image/jpeg',
                    0.6 // Compression quality
                );
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    };

    const handleSend = async () => {
        console.log("display name:", displayName);
        if (text.trim()) {
            sendMessage({
                sender: displayName,
                text,
                sessionId,
                autoHideInterval: isCustomInterval ? parseInt(selectedInterval, 10) : null
            }, 'chat', threadId);
            setText('');

            // Reset custom interval after sending
            if (isCustomInterval) {
                // Reset to thread default
                const currentThread = threads.find(thread => thread.id === threadId);
                if (currentThread && currentThread.config && currentThread.config.unhiddenInterval) {
                    setSelectedInterval(currentThread.config.unhiddenInterval.toString());
                } else {
                    setSelectedInterval('86400000');
                }
                setIsCustomInterval(false);
            }
        }
        if (compressedFile) {
            const reader = new FileReader();
            reader.onload = async () => {
                sendMessage({
                    sender: displayName,
                    image: compressedFile,
                    sessionId,
                    autoHideInterval: isCustomInterval ? parseInt(selectedInterval, 10) : null
                }, 'image', threadId);

                setCompressedFile(null);
                setPreviewImage(null); // Clear the preview

                // Reset custom interval after sending
                if (isCustomInterval) {
                    // Reset to thread default
                    const currentThread = threads.find(thread => thread.id === threadId);
                    if (currentThread && currentThread.config && currentThread.config.unhiddenInterval) {
                        setSelectedInterval(currentThread.config.unhiddenInterval.toString());
                    } else {
                        setSelectedInterval('86400000');
                    }
                    setIsCustomInterval(false);
                }
            };
            reader.readAsDataURL(compressedFile);
        }
    };

    const handleRemovePreview = () => {
        setCompressedFile(null);
        setPreviewImage(null);
    };

    const handleIntervalSelect = (value) => {
        if (value === 'none') {
            // Reset to default (unclicked state)
            const currentThread = threads.find(thread => thread.id === threadId);
            if (currentThread && currentThread.config && currentThread.config.unhiddenInterval) {
                setSelectedInterval(currentThread.config.unhiddenInterval.toString());
            } else {
                setSelectedInterval('86400000');
            }
            setIsCustomInterval(false);
        } else {
            // Set custom interval
            setSelectedInterval(value);
            setIsCustomInterval(true);
        }
        setShowIntervalDropdown(false);
    };

    const toggleIntervalDropdown = () => {
        setShowIntervalDropdown(!showIntervalDropdown);
    };

    return (
        <>
            {threads && threads.length > 0 && <div className="message-input-container">
                {previewImage && (
                    <div className="preview-container">
                        <div className="image-preview">
                            <img src={previewImage} alt="Preview" className="preview-image" />
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="remove-preview-icon"
                                onClick={handleRemovePreview}
                                title="Remove Image"
                            />
                        </div>
                    </div>
                )}
                <div className="input-main-line">
                    <label htmlFor="image-input" className="image-upload-icon">
                        <FontAwesomeIcon icon={faPlus} />
                    </label>
                    <input
                        type="file"
                        id="image-input"
                        style={{ display: 'none' }}
                        accept="image/jpeg"
                        onChange={handleImageSelect}
                    />

                    {/* Timer icon for setting message auto-hide */}
                    <div className="timer-icon-container" ref={dropdownRef}>
                        <FontAwesomeIcon
                            icon={faClock}
                            className={`timer-icon-input ${isCustomInterval ? 'custom-interval' : ''} ${!text.trim() && !compressedFile ? 'disabled' : ''}`}
                            onClick={text.trim() || compressedFile ? toggleIntervalDropdown : undefined}
                            title="Set message auto-hide interval"
                        />

                        {showIntervalDropdown && (
                            <div className="interval-dropdown">
                                <div className="interval-dropdown-header">Message Auto-hide</div>
                                <div className="interval-options">
                                    {intervalOptions.map(option => (
                                        <div
                                            key={option.value}
                                            className={`interval-option ${selectedInterval === option.value ? 'selected' : ''}`}
                                            onClick={() => handleIntervalSelect(option.value)}
                                            data-value={option.value}
                                        >
                                            {option.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="input-preview-container">
                        <input
                            type="text"
                            className="message-input full-width"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Type a message..."
                            disabled={!!previewImage} // Disable text input when an image is selected
                        />
                    </div>
                    {isMobile ? (
                        <button className="send-icon-button" onClick={handleSend} disabled={!text.trim() && !compressedFile}>
                            <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                    ) : (
                        <button
                            className="send-button full-width"
                            onClick={handleSend}
                            disabled={!text.trim() && !compressedFile}
                        >
                            Send
                        </button>
                    )}
                </div>
            </div>
            }
        </>
    );
};

export default MessageInput;
